import { useState } from 'react';
import { X } from 'lucide-react';

function FilterPill({ label, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={isSelected}
      className={`px-4 py-2 rounded-full text-sm transition-colors border ${
        isSelected
          ? 'bg-[var(--accent)] border-[var(--accent)] text-[var(--accent-foreground)]'
          : 'bg-card border-[var(--border)] text-muted-foreground hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );
}

export function FilterModal({
  isOpen,
  onClose,
  onApply,
  availableTags,
  timingTags = [],
  selectedMealTypes,
  selectedTags,
}) {
  const [tempMealTypes, setTempMealTypes] = useState(selectedMealTypes);
  const [tempTags, setTempTags] = useState(selectedTags);

  if (!isOpen) return null;

  const handleMealTypeToggle = (mealType) => {
    setTempMealTypes(prev =>
      prev.includes(mealType)
        ? prev.filter(t => t !== mealType)
        : [...prev, mealType]
    );
  };

  const handleTagToggle = (tag) => {
    setTempTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleApply = () => {
    onApply(tempMealTypes, tempTags);
  };

  const handleClearAll = () => {
    setTempMealTypes([]);
    setTempTags([]);
  };

  const handleClose = () => {
    // Reset to current filters on close without applying
    setTempMealTypes(selectedMealTypes);
    setTempTags(selectedTags);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/10 flex items-end sm:items-center justify-center z-50">
      <div className="bg-card text-foreground rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="text-xl">Filters</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Meal Type Section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-2">Meal Type</h3>
            <div className="flex flex-wrap gap-2">
              {['breakfast', 'lunch', 'dinner', 'dessert'].map((mealType) => (
                <FilterPill
                  key={mealType}
                  label={mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                  isSelected={tempMealTypes.includes(mealType)}
                  onClick={() => handleMealTypeToggle(mealType)}
                />
              ))}
            </div>
          </div>

          {/* Timing Section */}
          {timingTags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-2">Timing</h3>
              <div className="flex flex-wrap gap-2">
                {timingTags.map((tag) => (
                  <FilterPill
                    key={tag}
                    label={tag}
                    isSelected={tempTags.includes(tag)}
                    onClick={() => handleTagToggle(tag)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Other Tags Section */}
          <div>
            <h3 className="text-sm font-medium mb-2">Other</h3>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <FilterPill
                  key={tag}
                  label={tag}
                  isSelected={tempTags.includes(tag)}
                  onClick={() => handleTagToggle(tag)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 border-t border-[var(--border)] flex gap-3 bg-card">
          <button
            onClick={handleClearAll}
            className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] bg-card hover:bg-[var(--primary-light)] transition-colors"
            >
            Clear All
          </button>
          <button
            onClick={handleApply}
              className="flex-1 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
